import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { JOBS, JOBS_EMAIL } from "@/data/jobs";
import { Upload, FileText, X, CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const inputCls =
  "w-full bg-obsidian border border-white/15 text-white px-4 py-3.5 text-sm focus:border-brand-light focus:outline-none transition-colors placeholder:text-zinc-600";
const labelCls = "block text-[11px] uppercase tracking-wider text-zinc-400 mb-2";

export default function ApplicationForm({ position }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    city: "", position: position || "", start_date: "", message: "",
  });
  const [cv, setCv] = useState(null); // {url, name}
  const [docs, setDocs] = useState([]);
  const [privacy, setPrivacy] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const cvRef = useRef(null);
  const docsRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/applications/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data; // {url, name}
  };

  const onCv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setCv({ name: file.name, uploading: true });
      const up = await uploadFile(file);
      setCv(up);
    } catch (err) {
      setCv(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onDocs = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const file of files.slice(0, 5)) {
      try {
        const up = await uploadFile(file);
        setDocs((prev) => [...prev, up]);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!privacy) {
      toast.error("Bitte bestätige die Datenschutzerklärung.");
      return;
    }
    if (cv?.uploading) return;
    setSending(true);
    try {
      await api.post("/applications", {
        ...form,
        position: form.position || position,
        cv_url: cv?.url || "",
        document_urls: docs.map((d) => d.url),
        privacy_accepted: true,
      });
      setDone(true);
      document.getElementById("bewerben")?.scrollIntoView({ behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="bg-obsidian-surface border border-brand-light/40 p-8 md:p-12 text-center" data-testid="application-success">
        <CheckCircle2 className="w-14 h-14 text-brand-bright mx-auto mb-6" />
        <h3 className="font-heading font-black uppercase tracking-tight text-white text-2xl md:text-3xl mb-4">
          Bewerbung erhalten
        </h3>
        <p className="text-zinc-300 leading-relaxed max-w-xl mx-auto">
          Vielen Dank für deine Bewerbung bei DNA Automotive GmbH. Wir haben deine Unterlagen erfolgreich
          erhalten und werden uns schnellstmöglich bei dir melden.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-obsidian-surface border border-white/10 p-6 md:p-10" data-testid="application-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Vorname *</label>
          <input required value={form.first_name} onChange={set("first_name")} className={inputCls} data-testid="app-first-name" />
        </div>
        <div>
          <label className={labelCls}>Nachname *</label>
          <input required value={form.last_name} onChange={set("last_name")} className={inputCls} data-testid="app-last-name" />
        </div>
        <div>
          <label className={labelCls}>E-Mail-Adresse *</label>
          <input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="app-email" />
        </div>
        <div>
          <label className={labelCls}>Telefonnummer</label>
          <input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="app-phone" />
        </div>
        <div>
          <label className={labelCls}>Wohnort</label>
          <input value={form.city} onChange={set("city")} className={inputCls} data-testid="app-city" />
        </div>
        <div>
          <label className={labelCls}>Frühestmöglicher Eintrittstermin</label>
          <input value={form.start_date} onChange={set("start_date")} placeholder="z. B. ab sofort / 01.09.2026" className={inputCls} data-testid="app-start-date" />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Gewünschte Stelle *</label>
          <select required value={form.position} onChange={set("position")} className={inputCls} data-testid="app-position">
            <option value="" disabled>Bitte wählen</option>
            {JOBS.map((j) => (
              <option key={j.slug} value={j.title}>{j.title}</option>
            ))}
            <option value="Initiativbewerbung">Initiativbewerbung</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Kurze Nachricht / persönliche Vorstellung</label>
          <textarea rows={5} value={form.message} onChange={set("message")} className={inputCls} data-testid="app-message" placeholder="Erzähl uns kurz, wer du bist und warum du zu DNA Automotive passt." />
        </div>

        {/* Lebenslauf */}
        <div>
          <label className={labelCls}>Lebenslauf hochladen (PDF/JPG/PNG)</label>
          <input ref={cvRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" className="hidden" onChange={onCv} />
          {cv ? (
            <div className="flex items-center gap-3 border border-brand-light/40 bg-obsidian px-4 py-3.5" data-testid="app-cv-file">
              {cv.uploading ? <Loader2 className="w-4 h-4 text-brand-bright animate-spin" /> : <FileText className="w-4 h-4 text-brand-bright" />}
              <span className="text-sm text-white truncate flex-1">{cv.name}</span>
              <button type="button" onClick={() => setCv(null)} className="text-zinc-500 hover:text-red-400" aria-label="Entfernen">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => cvRef.current?.click()} className="w-full flex items-center justify-center gap-2 border border-dashed border-white/25 text-zinc-300 px-4 py-3.5 text-sm hover:border-brand-light hover:text-brand-bright transition-colors" data-testid="app-cv-upload">
              <Upload className="w-4 h-4" />
              Datei auswählen
            </button>
          )}
        </div>

        {/* Weitere Dokumente */}
        <div>
          <label className={labelCls}>Weitere Dokumente / Zeugnisse (optional)</label>
          <input ref={docsRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" className="hidden" onChange={onDocs} />
          <button type="button" onClick={() => docsRef.current?.click()} className="w-full flex items-center justify-center gap-2 border border-dashed border-white/25 text-zinc-300 px-4 py-3.5 text-sm hover:border-brand-light hover:text-brand-bright transition-colors" data-testid="app-docs-upload">
            <Upload className="w-4 h-4" />
            Dateien auswählen
          </button>
          {docs.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="app-docs-list">
              {docs.map((d, i) => (
                <div key={d.url} className="flex items-center gap-3 border border-white/10 px-3 py-2">
                  <FileText className="w-4 h-4 text-brand-bright shrink-0" />
                  <span className="text-xs text-zinc-300 truncate flex-1">{d.name}</span>
                  <button type="button" onClick={() => setDocs(docs.filter((_, idx) => idx !== i))} className="text-zinc-500 hover:text-red-400" aria-label="Entfernen">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Datenschutz */}
      <label className="flex items-start gap-3 mt-8 cursor-pointer" data-testid="app-privacy">
        <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-1 w-4 h-4 accent-[#2EE06B]" />
        <span className="text-zinc-400 text-xs leading-relaxed">
          Ich habe die <Link to="/datenschutz" className="text-brand-bright hover:underline" target="_blank">Datenschutzerklärung</Link> gelesen
          und bin mit der Verarbeitung meiner Daten zur Bearbeitung meiner Bewerbung einverstanden. *
        </span>
      </label>

      <button
        type="submit"
        disabled={sending || cv?.uploading}
        className="group mt-8 w-full inline-flex items-center justify-center gap-3 bg-brand text-black font-bold uppercase tracking-wider text-base px-8 py-5 hover:bg-brand-hover transition-colors disabled:opacity-50"
        data-testid="app-submit"
      >
        {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        Bewerbung absenden
      </button>
      <p className="text-zinc-600 text-[11px] text-center mt-4">
        Alternativ per E-Mail an <a href={`mailto:${JOBS_EMAIL}`} className="text-brand-bright">{JOBS_EMAIL}</a>
      </p>
    </form>
  );
}
